import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontSizeType, UserContactData } from '../../../types';
import Table, { ColumnsType } from 'antd/lib/table';
import { setFont } from '../../../utils/colComponents';
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { actionPermissions } from '../../../constants/actionPermissions';
import { hasPermission } from '../../../utils/hasPermission';
import {
  Badge,
  Button,
  Form,
  Input,
  Popover,
  Space,
  Tag,
  Typography,
} from 'antd';
import { RED1 } from '../../../constants/color';
import FourZeroThree from '../../FourZeroThree';
import FiveHundred from '../../FiveHundred';
import TableToolbar from '../../table/TableToolbar';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useVT } from 'virtualizedtableforantd4';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import TableFooterToolbar from '../../table/TableFooterToolbar';
import { alertMessage } from '../../../utils/alertMessage';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../../utils/axiosRequest';
import { tableScrollToTop } from '../../../utils/helperFunction';
import Popconfirm from 'antd/es/popconfirm';
import UserContactModal from '../UserContactModal';

type UserContactTableProps = {
  id?: string;
};
const UserContactTable = ({ id }: UserContactTableProps) => {
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const { t } = useTranslation();
  const [fourZeroThree, setFourZeroThree] = useState(false);
  const [fiveHundred, setFiveHundred] = useState<boolean>(false);
  const [contactData, setContactData] = useState<Array<UserContactData>>([]);
  const [loading, setLoading] = useState(false);
  const [vt] = useVT(() => ({ scroll: { y: 600 } }), []);
  const [showModal, setShowModal] = useState(false);
  const [editUserContact, setEditUserContact] = useState<
    UserContactData | undefined
  >();
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<UserContactData[]>([]);
  const isSubscribed = useRef(true);
  const screens = useBreakpoint();
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const [advance, setAdvance] = useState<{
    firstName?: string;
    lastName?: string;
    mobile?: string;
    email?: string;
  }>({});
  const [keyword, setKeyword] = useState('');
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage('pageSize', '10');
  const columnKeys = [
    'contactId',
    'mobile',
    'email',
    'firstName',
    'lastName',
    'position',
    'remark',
    'updateTime',
    'action',
  ];

  const [selectedColumns, setSelectedColumns] = useState(columnKeys);

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  const columns: ColumnsType<UserContactData> = [
    {
      title: setFont(t('users.userContactColumn.contactId'), fontSize),
      dataIndex: 'contactId',
      key: 'contactId',
      width: 50,
      fixed: screens.lg ? 'left' : false,
      render: (text: string, contact: UserContactData) => (
        <Button
          type="link"
          style={{ padding: 0, fontSize: fontSize }}
          disabled={!hasPermission(actionPermissions.userGroup.userManage)}
          onClick={() => onEdit(contact)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: setFont(t('users.userContactColumn.mobile'), fontSize),
      dataIndex: 'mobile',
      key: 'mobile',
      width: 80,
      render: (text: string, contact: UserContactData) => (
        <Space direction="vertical">
          {setFont(text, fontSize)}
          {contact.isDefault && (
            <Tag color={RED1} style={{ fontSize: fontSize }}>
              {t('users.userContactColumn.isDefault')}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: setFont(t('users.userContactColumn.email'), fontSize),
      dataIndex: 'email',
      key: 'email',
      width: 80,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('users.userContactColumn.firstName'), fontSize),
      dataIndex: 'firstName',
      key: 'firstName',
      width: 80,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('users.userContactColumn.lastName'), fontSize),
      dataIndex: 'lastName',
      key: 'lastName',
      width: 80,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('users.userContactColumn.position'), fontSize),
      dataIndex: 'position',
      key: 'position',
      width: 80,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('users.userContactColumn.remark'), fontSize),
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('users.userContactColumn.updateTime'), fontSize),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 80,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('actionsColumn.title'), fontSize),
      key: 'action',
      fixed: screens.lg ? 'right' : false,
      width: 120,
      render: (contact) => (
        <Space>
          <Button
            type="link"
            style={{ padding: 0, fontSize: fontSize }}
            onClick={() => onEdit(contact)}
            disabled={!hasPermission(actionPermissions.userGroup.userManage)}
          >
            {t('actionsColumn.edit')}
          </Button>
          {!getDashboardStyle().isSalesForceAppType && (
            <Popconfirm
              title={setFont(t('actionsColumn.deleteWarning'), fontSize)}
              onConfirm={() => {
                onDelete([contact.contactId]);
              }}
              okText={t('actionsColumn.confirmation.yes')}
              cancelText={t('actionsColumn.confirmation.no')}
              placement="topRight"
              disabled={!hasPermission(actionPermissions.userGroup.userManage)}
            >
              <Button
                danger
                type="link"
                style={{ padding: 0, fontSize: fontSize }}
                disabled={
                  !hasPermission(actionPermissions.userGroup.userManage)
                }
              >
                {t('actionsColumn.delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const advancedSearch = (
    <Popover
      overlayStyle={{ zIndex: 100 }}
      title={t('general.advancedSearch')}
      trigger="click"
      placement="bottomRight"
      content={
        <Form
          layout="vertical"
          form={form}
          ref={formRef}
          style={{ width: 250 }}
        >
          <Form.Item name="mobile" label={t('users.userContactColumn.mobile')}>
            <Input
              onChange={(e) =>
                setAdvance((prev) => ({
                  ...prev,
                  mobile: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item name="email" label={t('users.userContactColumn.email')}>
            <Input
              onChange={(e) =>
                setAdvance((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item
            name="firstName"
            label={t('users.userContactColumn.firstName')}
          >
            <Input
              onChange={(e) =>
                setAdvance((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('users.userContactColumn.lastName')}
          >
            <Input
              onChange={(e) =>
                setAdvance((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space wrap>
              <Button
                htmlType="submit"
                type="primary"
                onClick={() => {
                  setKeyword('');
                  if (page !== 1) setPage(1);
                  else {
                    if (typingTimeout) clearTimeout(typingTimeout);
                    setTypingTimeout(
                      setTimeout(() => getUserContactData(), GENERAL_TIMEOUT)
                    );
                  }
                }}
              >
                {t('general.search')}
              </Button>
              <Button
                disabled={Object.values(advance).every((value) => !value)}
                onClick={() => {
                  form.resetFields();
                  setAdvance({});
                  if (page !== 1) setPage(1);
                  else getUserContactData();
                }}
              >
                {t('general.reset')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      }
    >
      <Badge
        count={Object.keys(advance).reduce((accumulator, obj) => {
          if (advance[obj as keyof typeof advance]) {
            return accumulator + 1;
          }

          return accumulator;
        }, 0)}
      >
        <Button icon={<FilterOutlined />}>{t('general.advancedSearch')}</Button>
      </Badge>
    </Popover>
  );

  const getUserContactData = useCallback(() => {
    if (isSubscribed.current) setLoading(true);
    getDataWithAuthToken('users/contact/list', {
      params: {
        keyword: keyword || undefined,
        userId: id ? id : undefined,
        mobile: (formRef.current && form.getFieldValue('mobile')) || undefined,
        email: (formRef.current && form.getFieldValue('email')) || undefined,
        firstName:
          (formRef.current && form.getFieldValue('firstName')) || undefined,
        lastName:
          (formRef.current && form.getFieldValue('lastName')) || undefined,
      },
    })
      .then((result) => {
        if (isSubscribed.current) {
          if (result && result.goodStatus && result.data) {
            if (isSubscribed.current) {
              setContactData(result.data.list);
              setTotalItems(result.data.totalItem);
              setSelectedRowKeys([]);
              setLoading(false);
              // Scroll to top when data changes
              tableScrollToTop();
            }

            // setLoading(false);
          } else if (result && result.returnCode === 403) {
            if (isSubscribed.current) setFourZeroThree(true);
          } else {
            setFiveHundred(true);
            setLoading(false);
            alertMessage(
              'error',
              result?.msg || t('general.noResponse'),
              result?.data || undefined
            );
          }
        }
      })
      .catch((err) => {
        if (isSubscribed.current) setLoading(false);
        console.log(err);
      });
  }, [t, keyword, form, id]);

  useEffect(() => {
    getUserContactData();
  }, [getUserContactData]);

  const onEdit = (userContact: UserContactData) => {
    if (isSubscribed.current) {
      setShowModal(true);
      setEditUserContact(userContact);
    }
  };

  const onDelete = (ids: number[]) => {
    postDataWithAuthToken('users/contact/delete_batch', {
      contactIds: ids,
    })
      .then((result) => {
        if (result && result.goodStatus) {
          getUserContactData();
          alertMessage('success', t('users.alerts.userContactDeleted'));
        } else
          alertMessage(
            'error',
            result?.msg || t('general.noResponse'),
            result?.data || undefined
          );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return fourZeroThree ? (
    <FourZeroThree />
  ) : fiveHundred ? (
    <FiveHundred />
  ) : (
    <>
      {id ? (
        <Typography.Title level={4} style={{ marginBottom: 12 }}>
          {t('users.userDetailPage.userContact')}
        </Typography.Title>
      ) : (
        <h1 style={{ fontSize: 24 }}>
          {t('users.userDetailPage.userContact')}
        </h1>
      )}
      <TableToolbar
        leftElement={
          id ? (
            <Button
              disabled={!hasPermission(actionPermissions.userGroup.userManage)}
              icon={<PlusOutlined />}
              onClick={() => {
                setEditUserContact(undefined);
                setShowModal(true);
              }}
            >
              {t('users.userContactColumn.addTitle')}
            </Button>
          ) : undefined
        }
        setFontSize={setFontSize}
        fontSize={fontSize}
        tableSize={tableSize}
        setTableSize={setTableSize}
        refresh={() => getUserContactData()}
        totalItems={totalItems}
        columns={columns}
        columnKeys={columnKeys}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        search={(keyword) => {
          setKeyword(keyword);
          setPage(1);
        }}
        searchPlaceholder={t('searchPlaceholders.searchUserContactKeyword')}
        advancedSearch={advancedSearch}
        rows={contactData.map((contact) => ({
          ...contact,
        }))}
        exportConfig={{ fileName: 'USER_CONTACT' }}
      />
      <Table<UserContactData>
        loading={loading}
        columns={columns.filter((x) =>
          selectedColumns.includes(x.key?.toString() ?? '')
        )}
        components={vt}
        scroll={{ y: 600, x: 1200 }}
        dataSource={contactData}
        rowKey={(contact) => contact.contactId}
        size={tableSize}
        pagination={{
          total: totalItems,
          pageSize: pageSize,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) =>
            t('general.paginationTotal', {
              start: range[0],
              end: range[1],
              total: total,
            }),
          size: 'small',
          onChange: (page, size) => {
            setPage(page);
            setPageSize(size || pageSize);
            setSelectedRowKeys([]);
          },
          defaultPageSize: pageSize,
          current: page,
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (
            selectedRowKeys: React.Key[],
            selectedRows: UserContactData[]
          ) => {
            setSelectedRowKeys(selectedRowKeys);
            setSelectedRows(selectedRows);
          },
        }}
      />
      {selectedRowKeys.length > 0 && (
        <TableFooterToolbar
          typeSpecificActions="userContact"
          selectedRows={selectedRows.map((contact) => ({
            ...contact,
          }))}
          setSelectedRowKeys={setSelectedRowKeys}
          columns={columns.filter((x) =>
            selectedColumns.includes(x.key?.toString() ?? '')
          )}
          general={
            hasPermission(actionPermissions.userGroup.userManage)
              ? { advance: advance, setAdvance: setAdvance }
              : undefined
          }
          funct={{
            deleteFunc:
              hasPermission(actionPermissions.userGroup.userManage) &&
              !getDashboardStyle().isSalesForceAppType
                ? () => onDelete(selectedRowKeys)
                : undefined,
            exportConfig: { fileName: 'USER_CONTACT' },
          }}
        />
      )}
      <UserContactModal
        visible={showModal}
        setVisible={setShowModal}
        editUserContact={editUserContact}
        refresh={() => getUserContactData()}
        userId={id}
      />
    </>
  );
};

export default UserContactTable;
